import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { fetchDocumentFresh, fetchPreviewEntry, saveArticle } from '../api/contentApi';
import { articleInfo } from '../content/articleInfo';
import { MediaContext } from '../content/renderer/MediaContext';
import { applyPreviewSeo } from '../content/seo';
import { getTemplate } from '../content/templates';
import StatusBadge from '../components/StatusBadge';
import StatusMessage from '../components/StatusMessage';
import LoadError from '../components/LoadError';
import { SITE_NAME } from '../siteConfig';
import ArticleEditor from '../preview/ArticleEditor';
import { usePreviewAuth } from '../preview/PreviewGuard';
import { buildChanges, formFromArticle, validateForm } from '../preview/changes';
import useAsync from '../useAsync';

async function load(slug) {
  const entry = await fetchPreviewEntry(slug);
  if (!entry) return { entry: null };
  return { entry, doc: await fetchDocumentFresh(entry) };
}

export default function PreviewPage() {
  const { slug } = useParams();
  const { lock } = usePreviewAuth();
  const [reload, setReload] = useState(0);
  const { loading, error, data } = useAsync(() => load(slug), [slug, reload]);
  const [form, setForm] = useState(null);
  const [baseline, setBaseline] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  // (Re)start the form from whatever is on the server.
  useEffect(() => {
    if (!data?.entry) return;
    const fresh = formFromArticle(data.entry, data.doc);
    setForm(fresh);
    setBaseline(fresh);
  }, [data]);

  useEffect(() => {
    if (data?.doc) applyPreviewSeo(data.doc.meta?.title || slug, SITE_NAME);
  }, [data, slug]);

  const built = useMemo(() => (data?.doc && form ? buildChanges(data.doc, form) : null), [data, form]);
  const errors = useMemo(() => (data?.entry && form ? validateForm(data.entry, form) : {}), [data, form]);
  const dirty = form && baseline && JSON.stringify(form) !== JSON.stringify(baseline);

  // Warn before leaving with unsaved changes.
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  async function onSave() {
    setSaving(true);
    setMessage(null);
    const publishing = form.status === 'published' && String(data.entry.status).toLowerCase() !== 'published';
    try {
      const { docData, rowData } = buildChanges(data.doc, form);
      await saveArticle(data.entry, docData, rowData);
      setMessage({ type: 'ok', text: publishing ? 'Saved. The article is now published.' : 'Saved.' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
      setReload((n) => n + 1); // show what the server really holds now
    }
  }

  if (loading && !data) return <div className="blog-container"><StatusMessage title="Loading preview…" /></div>;
  if (error) {
    return (
      <div className="blog-container">
        <LoadError error={error} onRetry={() => setReload((n) => n + 1)} technical what="this article" />
        <p className="center"><Link to="/preview">Back to all articles</Link></p>
      </div>
    );
  }
  if (!data?.entry) {
    return (
      <div className="blog-container">
        <StatusMessage title="Article not found">There is no article with the address “{slug}”.</StatusMessage>
        <p className="center"><Link to="/preview">Back to all articles</Link></p>
      </div>
    );
  }
  if (!form || !built) return null;

  const Template = getTemplate(built.doc.type);
  const info = { ...articleInfo(built.doc), status: form.status };

  return (
    <>
      <div className="preview-bar">
        <div className="blog-container preview-bar-inner">
          <span className="preview-label">Preview</span>
          <span>Only people with this link and the password can see this page.</span>
          <StatusBadge status={data.entry.status} />
          <span className="spacer" />
          <Link to="/preview">All articles</Link>
          <button type="button" className="link-button" onClick={lock}>Lock</button>
        </div>
      </div>
      <div className="blog-container preview-layout">
        <div className="preview-article">
          <MediaContext.Provider value={built.doc.media || {}}>
            <Template entry={info} doc={built.doc} />
          </MediaContext.Provider>
        </div>
        <aside className="preview-editor" aria-label="Article details">
          <ArticleEditor
            entry={data.entry}
            doc={data.doc}
            form={form}
            setForm={setForm}
            errors={errors}
            dirty={Boolean(dirty)}
            saving={saving}
            message={message}
            onSave={onSave}
            onRevert={() => {
              setForm(baseline);
              setMessage(null);
            }}
          />
        </aside>
      </div>
    </>
  );
}
